import { lastValueFrom } from 'rxjs';
import { CacheMap } from '../classes';
import { HttpClient } from '@angular/common/http';
import { APIURL } from 'app/shared/constants';
import { Injectable } from '@angular/core';
import { Country, PhonePrefix, UserType, Language, State } from '@app/models';
import { TranslateService } from '@ngx-translate/core';

@Injectable({ providedIn: 'root' })
export class DictionaryService {
  private BASE_DICTIONARY_URL = `${APIURL}dictionary/`;

  private _languages?: CacheMap<Language>;
  private _countries?: CacheMap<Country>;
  private _phonePrefixes?: CacheMap<PhonePrefix>;
  private _userTypes?: CacheMap<UserType>;

  private _availableLanguages: string[] = [
    'it',
    'en',
    'fr',
    'de',
    'es',
    'pt',
    'el',
    'pl',
    'cs',
    'ro',
    'et',
    'sk',
    'tr',
    'no',
    'fi',
    'sv',
    'da',
    'hu',
    'nl',
    'bg',
    'lv',
    'lt',
    'sr',
    'hr',
    'sl',
    'ru',
    'uk',
  ];

  constructor(
    private http: HttpClient,
    private translate: TranslateService,
  ) {}

  cleanCache() {
    this._languages?.clear();
    this._countries?.clear();
    this._phonePrefixes?.clear();
  }

  private url(endpoint: string): string {
    return `${this.BASE_DICTIONARY_URL}${endpoint}`;
  }

  async getLanguages(): Promise<CacheMap<Language>> {
    if (this._languages?.isNotEmpty) {
      return this._languages;
    }

    const url = this.url('languages');
    const response = await lastValueFrom(this.http.get<Language[]>(url));
    const sortedData = response
      .map((lang) => {
        return { ...lang, disabled: !this._availableLanguages.includes(lang.languageCode) };
      })
      .sort((a, b) => (a.name > b.name ? 1 : -1));
    this._languages = new CacheMap(sortedData, 'languageId');
    return this._languages;
  }

  async getCountries(): Promise<CacheMap<Country>> {
    if (this._countries?.isNotEmpty) {
      return this._countries;
    }

    const url = this.url('countries');
    const response = await lastValueFrom(this.http.get<Country[]>(url));

    const sortedData = response
      .map((country) => {
        return { ...country, disabled: false };
      })
      .sort((a, b) => {
        const aValue = this.translate.instant(`DB.COUNTRIES.CODE.${a.countryCode}`);
        const bValue = this.translate.instant(`DB.COUNTRIES.CODE.${b.countryCode}`);
        return aValue > bValue ? 1 : -1;
      });
    this._countries = new CacheMap(sortedData, 'countryId');
    return this._countries;
  }

  async getPhonePrefixes(): Promise<CacheMap<PhonePrefix>> {
    if (this._phonePrefixes?.isNotEmpty) {
      return this._phonePrefixes;
    }

    const url = this.url('phonePrefixes');
    const response = await lastValueFrom(this.http.get<PhonePrefix[]>(url));
    this._phonePrefixes = new CacheMap<PhonePrefix>(response, 'phonePrefixId');
    return this._phonePrefixes;
  }

  async getStates(): Promise<CacheMap<State>> {
    const url = this.url('states');
    const response = await lastValueFrom(this.http.get<State[]>(url));
    return new CacheMap<State>(response, 'stateId');
  }
}
